import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import { UserType } from 'graphql-ts-types';
import type {
  BorderRadiusProps,
  BorderStyleProps,
  BorderWidthProps,
  OpacityProps,
  PositionProps,
} from 'styled-system';
import {
  borderColor,
  borderRadius,
  borderStyle,
  borderWidth,
  opacity,
  position,
  themeGet,
} from 'styled-system';

import { FieldChangeTrigger } from '../../enums/form';
import { useLocalization } from '../../providers/LocaleProvider';
import { outlineCSSWithOffset } from '../../theme/styles';
import type { BorderColorProps } from '../../theme/system-types';
import type { FieldChangeTriggerHandler } from '../../types/form';
import { ButtonSecondary_V2 } from '../Button/Secondary';
import { HeadlineH8 } from '../Typography';
import VisuallyHidden from '../VisuallyHidden';

interface UserTypeFieldProps<FieldName extends string = string> {
  value?: string;
  name?: FieldName;
  onChange?: FieldChangeTriggerHandler<FieldName>;
  disabled?: boolean;
}

const RADIO_NAME = 'user-type';
const PRIVATE_RADIO_ID = 'user-type-private';
const BUSINESS_RADIO_ID = 'user-type-business';

const Fieldset = styled.fieldset`
  margin: 0;
  border: 0;
  padding: 0;
`;

const Input = styled.input<PositionProps & OpacityProps>`
  ${position};
  ${opacity};

  &:focus-visible + label {
    ${outlineCSSWithOffset};
  }
`;
const inputStyles: ComponentProps<typeof Input> = {
  position: 'absolute',
  opacity: 0,
};

const RadioInput = (props: ComponentProps<typeof Input>) => {
  return <Input type="radio" {...inputStyles} {...props} />;
};

const UserTypeLabelBase = styled(ButtonSecondary_V2)`
  &:hover {
    color: ${({ isActive, disabled }) =>
      isActive && !disabled
        ? themeGet('colors.sys.primary.text.hover')
        : themeGet('colors.sys.neutral.text.default')};
    border-color: ${({ isActive, disabled }) =>
      isActive && !disabled
        ? themeGet('colors.sys.primary.background.default')
        : themeGet('colors.sys.neutral.border.hover')};
  }
`;

const LabelText = styled(HeadlineH8)<
  BorderWidthProps & BorderStyleProps & BorderColorProps & BorderRadiusProps
>`
  ${borderWidth};
  ${borderStyle};
  ${borderColor};
  ${borderRadius}
`;

const UserTypeLabel = ({
  isActive,
  children,
  disabled,
  ...props
}: ComponentProps<typeof ButtonSecondary_V2>) => {
  return (
    <UserTypeLabelBase
      as="label"
      mr="sp_12"
      px="sp_0"
      py="sp_0"
      disabled={disabled}
      {...(isActive && !disabled
        ? {
            isActive,
            color: 'sys.primary.text.default',
            borderColor: 'sys.primary.background.default',
          }
        : {})}
      {...props}
    >
      <LabelText
        as="span"
        px="sp_16"
        py="sp_12"
        color="inherit"
        borderWidth="small"
        borderStyle="solid"
        borderRadius="2px"
        borderColor="transparent"
        {...(isActive && !disabled
          ? {
              borderColor: 'sys.primary.background.default',
            }
          : {})}
      >
        {children}
      </LabelText>
    </UserTypeLabelBase>
  );
};

const UserTypeField = ({ value, name, onChange, disabled }: UserTypeFieldProps) => {
  const { t } = useLocalization();

  const selectedType = value as UserType;

  const handleChange = (newValue: UserType) => {
    onChange?.({
      fieldName: name ?? '',
      value: newValue,
      trigger: FieldChangeTrigger.Change,
    });
  };

  return (
    <Fieldset>
      <VisuallyHidden as="legend">{t('h24_user_form_type')}</VisuallyHidden>
      <RadioInput
        name={RADIO_NAME}
        disabled={disabled}
        id={PRIVATE_RADIO_ID}
        value={UserType.Private}
        onChange={() => handleChange(UserType.Private)}
        defaultChecked={selectedType === UserType.Private}
      />
      <UserTypeLabel
        disabled={disabled}
        htmlFor={PRIVATE_RADIO_ID}
        isActive={selectedType === UserType.Private}
      >
        {t('h24_private_form_type')}
      </UserTypeLabel>

      <RadioInput
        name={RADIO_NAME}
        disabled={disabled}
        id={BUSINESS_RADIO_ID}
        value={UserType.Business}
        onChange={() => handleChange(UserType.Business)}
        defaultChecked={selectedType === UserType.Business}
      />
      <UserTypeLabel
        disabled={disabled}
        htmlFor={BUSINESS_RADIO_ID}
        isActive={selectedType === UserType.Business}
      >
        {t('h24_business_form_type')}
      </UserTypeLabel>
    </Fieldset>
  );
};

export default UserTypeField;
